import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { SlidersHorizontal, Grid, List, ChevronDown } from 'lucide-react';
import { ProductGrid } from '../components/products/ProductGrid';
import { ShopFilters } from '../components/shop/ShopFilters';

const API_URL = '';

const FALLBACK_CATEGORIES = [
  { id: 'nouvel-arrivage', name: 'Nouvel Arrivage' },
  { id: 'pyjama', name: 'Pyjama' },
  { id: 'sous-vetements', name: 'Sous Vetements' },
  { id: 'ensembles', name: 'Ensembles' },
  { id: 'nuisette', name: 'Nuisette' },
  { id: 'deshabille', name: 'Deshabille' },
  { id: 'kimono', name: 'Kimono' },
  { id: 'corset-body', name: 'Corset & Body' },
  { id: 'combishort', name: 'Combishort' },
  { id: 'sport', name: 'Sport' },
  { id: 'beaute', name: 'Beaute' },
  { id: 'accessoires', name: 'Accessoires' },
  { id: 'promotion', name: 'Promotion' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Plus récents' },
  { value: 'price_asc', label: 'Prix croissant' },
  { value: 'price_desc', label: 'Prix décroissant' },
  { value: 'popular', label: 'Populaire' },
];

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [bestsellerIds, setBestsellerIds] = useState(new Set());

  useEffect(() => {
    fetch(`${API_URL}/api/categories`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.categories?.length) setCategories(d.categories); })
      .catch(() => {});
    fetch(`${API_URL}/api/products/bestsellers?limit=12`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { 
        if (d?.products) {
          const ids = d.products.filter(p => p.is_top_seller).map(p => p.product_id);
          setBestsellerIds(new Set(ids)); 
        }
      })
      .catch(() => {});
  }, []);

  const filters = {
    category: searchParams.get('category'),
    size: searchParams.get('size'),
    minPrice: searchParams.get('min_price') ? Number(searchParams.get('min_price')) : null,
    maxPrice: searchParams.get('max_price') ? Number(searchParams.get('max_price')) : null,
    isNew: searchParams.get('is_new') === 'true',
    isBestseller: searchParams.get('is_bestseller') === 'true',
    isBridal: searchParams.get('is_bridal') === 'true',
    isNouvelArrivage: searchParams.get('is_nouvel_arrivage') === 'true',
    isPromotion: searchParams.get('is_promotion') === 'true',
    search: searchParams.get('search'),
  };

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.category) params.append('category', filters.category);
        if (filters.size) params.append('size', filters.size);
        if (filters.minPrice) params.append('min_price', filters.minPrice);
        if (filters.maxPrice) params.append('max_price', filters.maxPrice);
        if (filters.isNew) params.append('is_new', 'true');
        if (filters.isBestseller) params.append('is_bestseller', 'true');
        if (filters.isBridal) params.append('is_bridal', 'true');
        if (filters.isNouvelArrivage) params.append('is_nouvel_arrivage', 'true');
        if (filters.isPromotion) params.append('is_promotion', 'true');
        if (filters.search) params.append('search', filters.search);
        params.append('page', page);
        params.append('limit', 20);

        const response = await fetch(`${API_URL}/api/products?${params.toString()}`);
        if (response.ok) {
          const data = await response.json();
          const enriched = (data.products || []).map(p => 
            bestsellerIds.has(p.product_id) ? { ...p, is_top_seller: true } : p
          );
          setProducts(enriched);
          setTotal(data.total || 0);
          setPages(data.pages || 1);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [filters.category, filters.size, filters.minPrice, filters.maxPrice, filters.isNew, filters.isBestseller, filters.isBridal, filters.isNouvelArrivage, filters.isPromotion, filters.search, page, bestsellerIds]);

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    const paramName = key === 'minPrice' ? 'min_price'
      : key === 'maxPrice' ? 'max_price'
      : key === 'isNew' ? 'is_new'
      : key === 'isBestseller' ? 'is_bestseller'
      : key === 'isBridal' ? 'is_bridal'
      : key === 'isNouvelArrivage' ? 'is_nouvel_arrivage'
      : key === 'isPromotion' ? 'is_promotion'
      : key;
    if (value === null || value === false) {
      newParams.delete(paramName);
    } else {
      newParams.set(paramName, value);
    }
    setSearchParams(newParams);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchParams({});
    setPage(1);
  };

  const getCategoryName = () => {
    if (filters.category) {
      const cat = categories.find(c => c.id === filters.category);
      return cat ? cat.name : 'Boutique';
    }
    if (filters.isNew) return 'Nouveautés';
    if (filters.isBestseller) return 'Meilleures Ventes';
    if (filters.isBridal) return 'Collection Mariée';
    if (filters.isNouvelArrivage) return 'Nouvel Arrivage';
    if (filters.isPromotion) return 'Promotions';
    if (filters.search) return `Résultats pour "${filters.search}"`;
    return 'Tous les Produits';
  };

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="shop-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="breadcrumb flex items-center gap-2">
          <Link to="/" className="hover:text-[#1A1514]">Accueil</Link>
          <span>/</span>
          <span className="text-[#1A1514]">{getCategoryName()}</span>
        </nav>
      </div>

      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-serif text-4xl mb-2">{getCategoryName()}</h1>
        <p className="text-[#6B6A68]">{total} produits</p>
      </div>

      {/* Toolbar */}
      <div className="border-y border-[#E0DFDD] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Mobile Filter Toggle */}
            <button
              onClick={() => setShowMobileFilters(true)}
              className="lg:hidden flex items-center gap-2 text-sm"
              data-testid="mobile-filter-btn"
            >
              <SlidersHorizontal size={18} />
              Filtres
            </button>

            {/* Sort */}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-[#6B6A68] hidden sm:block">Trier par:</span>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-transparent pr-8 text-sm font-medium cursor-pointer focus:outline-none"
                  data-testid="sort-select"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex gap-12">
          {/* Desktop Filters */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <ShopFilters
              categories={categories}
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />
          </div>

          {/* Products */}
          <div className="flex-1">
            <ProductGrid products={products} loading={loading} />

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex justify-center gap-2 mt-12">
                {[...Array(pages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`w-10 h-10 flex items-center justify-center text-sm transition-colors ${
                      page === i + 1
                        ? 'bg-[#1A1514] text-white'
                        : 'bg-white border border-[#E0DFDD] hover:border-[#1A1514]'
                    }`}
                    data-testid={`page-${i + 1}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters */}
      <ShopFilters
        categories={categories}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        isMobile
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
      />
    </div>
  );
}
